import React from "react";
import { translations } from "../../utils/translations";

/**
 * ======================================================================
 * MentionsLegalesView.jsx
 * ======================================================================
 *
 * Page d'informations légales du site Marché Deluxe.
 *
 * Cette page présente :
 * - l'identité juridique de l'entreprise
 * - l'adresse
 * - les coordonnées de contact
 * - les conditions d'utilisation générales
 */

const sectionStyle = {
  background: "rgba(255,255,255,0.96)",
  border: "1px solid rgba(0,0,0,0.08)",
  borderRadius: 16,
  padding: 24,
  boxShadow: "0 10px 28px rgba(0,0,0,0.05)",
};

const labelStyle = {
  fontSize: 12,
  opacity: 0.7,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  marginBottom: 6,
};

const valueStyle = {
  fontSize: 16,
  fontWeight: 600,
  lineHeight: 1.5,
};

const getActiveLang = () => {
  const saved = localStorage.getItem("lang");
  if (saved && ["fr", "de", "en"].includes(saved)) return saved;
  return "fr";
};

const MentionsLegalesView = () => {
  const lang = getActiveLang();
  const t = translations[lang] || translations.fr;

  const usageItems = Array.isArray(t?.legalUsageItems) ? t.legalUsageItems : [];

  return (
    <div
      style={{
        maxWidth: 1100,
        margin: "0 auto",
        padding: "40px 20px 80px",
      }}
    >
      <div style={{ marginBottom: 28 }}>
        <h1
          style={{
            margin: 0,
            fontSize: "2.2rem",
            lineHeight: 1.15,
            fontFamily: "serif",
            color: "#1f1a17",
          }}
        >
          {t.legalPageTitle || "Mentions légales"}
        </h1>

        <p
          style={{
            marginTop: 12,
            fontSize: 15,
            lineHeight: 1.7,
            color: "#4b433d",
            maxWidth: 820,
          }}
        >
          {t.legalPageSubtitle ||
            "Cette page présente les informations légales relatives à l’exploitation de la boutique Marché Deluxe ainsi que les conditions générales d’utilisation du site."}
        </p>
      </div>

      {/* IDENTITÉ ENTREPRISE */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 18,
          marginBottom: 28,
        }}
      >
        <section style={sectionStyle}>
          <div style={labelStyle}>
            {t.legalCompanyNameLabel || "Nom de l’entreprise"}
          </div>
          <div style={valueStyle}>
            {t.legalCompanyNameValue ||
              "La Maison du Fromage Neuchâtel - Sterchi Sàrl"}
          </div>
        </section>

        <section style={sectionStyle}>
          <div style={labelStyle}>
            {t.legalLegalFormLabel || "Forme juridique"}
          </div>
          <div style={valueStyle}>
            {t.legalLegalFormValue || "Société à responsabilité limitée (Sàrl)"}
          </div>
        </section>

        <section style={sectionStyle}>
          <div style={labelStyle}>{t.legalVatLabel || "Numéro TVA"}</div>
          <div style={valueStyle}>
            {t.legalVatValue || "CHE-192.944.984 TVA"}
          </div>
        </section>
      </div>

      {/* COORDONNÉES */}
      <section style={{ ...sectionStyle, marginBottom: 28 }}>
        <h2
          style={{
            marginTop: 0,
            marginBottom: 18,
            fontSize: "1.35rem",
            fontFamily: "serif",
            color: "#1f1a17",
          }}
        >
          {t.legalSectionContact || "Coordonnées"}
        </h2>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 18,
          }}
        >
          <div>
            <div style={labelStyle}>{t.legalAddressLabel || "Adresse"}</div>
            <div style={valueStyle}>
              rue du Trésor 2 bis
              <br />
              2000 Neuchâtel
              <br />
              Suisse
            </div>
          </div>

          <div>
            <div style={labelStyle}>Contact</div>
            <div style={valueStyle}>
              {t.legalPhoneLabel || "Téléphone"} :{" "}
              {t.legalPhoneValue || "+41 32 725 26 36"}
              <br />
              {t.legalEmailLabel || "Email"} :{" "}
              {t.legalEmailValue || "neuch@sterchi-fromages.ch"}
            </div>
          </div>
        </div>
      </section>

      {/* CONDITIONS UTILISATION */}
      <section style={sectionStyle}>
        <h2
          style={{
            marginTop: 0,
            marginBottom: 18,
            fontSize: "1.35rem",
            fontFamily: "serif",
            color: "#1f1a17",
          }}
        >
          {t.legalUsageTitle || "Conditions d’utilisation"}
        </h2>

        <div
          style={{
            display: "grid",
            gap: 16,
            fontSize: 15,
            lineHeight: 1.8,
            color: "#312a25",
          }}
        >
          {usageItems.length > 0 ? (
            usageItems.map((item, index) => (
              <p key={`legal-usage-${index}`} style={{ margin: 0 }}>
                {item}
              </p>
            ))
          ) : (
            <>
              <p style={{ margin: 0 }}>
                L’utilisation du site Marché Deluxe implique l’acceptation d’un
                usage conforme à sa destination, à savoir la consultation des
                produits, la préparation d’achats, la passation de commandes et
                l’accès à l’espace client.
              </p>

              <p style={{ margin: 0 }}>
                L’utilisateur s’engage à fournir des informations exactes lors
                de la création d’un compte, de la passation de commande et lors
                de toute communication avec la boutique.
              </p>

              <p style={{ margin: 0 }}>
                Toute utilisation frauduleuse, abusive ou portant atteinte au
                bon fonctionnement du site pourra entraîner une restriction
                d’accès ou toute mesure appropriée décidée par l’exploitant.
              </p>

              <p style={{ margin: 0 }}>
                Les contenus présents sur le site (textes, visuels, éléments
                graphiques, photographies et informations) sont protégés et ne
                peuvent être reproduits, copiés ou exploités sans autorisation
                préalable.
              </p>

              <p style={{ margin: 0 }}>
                La disponibilité du site ne peut être garantie en permanence.
                L’exploitant se réserve le droit de suspendre ou modifier tout
                ou partie du service pour maintenance, mise à jour ou raisons
                techniques.
              </p>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default MentionsLegalesView;
